import { SOURCE } from '@alert-manager/lib';
import React from 'react';
import { useTheme } from '../contexts/ThemeContext';

interface FilterState {
  environment: string;
  source: string;
  status: string;
  severity: string;
  project: string;
  timeRange: string;
  acknowledgedStatus: string;
}

interface FilterControlsProps {
  filters: FilterState;
  onFilterChange: (filterType: keyof FilterState, value: string) => void;
  totalResults?: number;
}

const FilterControls: React.FC<FilterControlsProps> = ({ filters, onFilterChange }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const selectStyle: React.CSSProperties = {
    backgroundColor: 'var(--input-bg)',
    borderColor: 'var(--input-border)',
    color: 'var(--text-primary)',
  };

  const selectClass = `border text-xs rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500/40 appearance-none cursor-pointer font-medium`;

  const hasActiveFilters =
    filters.source !== 'all' ||
    filters.status !== 'all' ||
    filters.severity !== 'all' ||
    filters.acknowledgedStatus !== 'unacknowledged' ||
    filters.project !== '' ||
    filters.timeRange !== '48h';

  const resetFilters = () => {
    onFilterChange('source', 'all');
    onFilterChange('status', 'all');
    onFilterChange('severity', 'all');
    onFilterChange('acknowledgedStatus', 'unacknowledged');
    onFilterChange('project', '');
    onFilterChange('timeRange', '48h');
  };

  return (
    <div className="flex flex-wrap items-center gap-2 py-2">
      <span className="text-[11px] uppercase tracking-wider font-semibold mr-1" style={{ color: 'var(--text-muted)' }}>
        Filters
      </span>

      <select value={filters.timeRange} onChange={(e) => onFilterChange('timeRange', e.target.value)} className={selectClass} style={selectStyle}>
        <option value="1h">1h</option>
        <option value="6h">6h</option>
        <option value="24h">24h</option>
        <option value="48h">48h</option>
        <option value="7d">7d</option>
        <option value="30d">30d</option>
        <option value="all">All time</option>
      </select>

      <select value={filters.status} onChange={(e) => onFilterChange('status', e.target.value)} className={selectClass} style={selectStyle}>
        <option value="all">Any status</option>
        <option value="ongoing">Ongoing</option>
        <option value="resolved">Resolved</option>
      </select>

      <select value={filters.severity} onChange={(e) => onFilterChange('severity', e.target.value)} className={selectClass} style={selectStyle}>
        <option value="all">Any severity</option>
        <option value="critical">Critical</option>
        <option value="high">High</option>
        <option value="warning">Warning</option>
        <option value="info">Info</option>
      </select>

      <select value={filters.source} onChange={(e) => onFilterChange('source', e.target.value)} className={selectClass} style={selectStyle}>
        <option value="all">Any source</option>
        <option value={SOURCE.GRAFANA}>Grafana</option>
        <option value={SOURCE.API}>API</option>
        <option value={SOURCE.WEBHOOK}>Webhook</option>
        <option value={SOURCE.WEBSOCKET}>WebSocket</option>
        <option value={SOURCE.DRUID}>Druid</option>
        <option value={SOURCE.ANYWHERE}>Anywhere</option>
      </select>

      <select
        value={filters.acknowledgedStatus}
        onChange={(e) => onFilterChange('acknowledgedStatus', e.target.value)}
        className={selectClass}
        style={selectStyle}
      >
        <option value="all">Any ack status</option>
        <option value="unacknowledged">Unacknowledged</option>
        <option value="acknowledged">Acknowledged</option>
      </select>

      <input
        type="text"
        value={filters.project}
        onChange={(e) => onFilterChange('project', e.target.value)}
        placeholder="Project..."
        className="border text-xs rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500/40 w-24 font-medium"
        style={{ ...selectStyle, '--placeholder-color': 'var(--text-muted)' } as React.CSSProperties}
      />

      {hasActiveFilters && (
        <button
          onClick={resetFilters}
          className={`text-[11px] font-medium px-2 py-1 rounded transition-colors ${isDark ? 'text-slate-400 hover:text-white hover:bg-white/10' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'}`}
        >
          Reset
        </button>
      )}
    </div>
  );
};

export default FilterControls;
